// Camada "Alertas do Waze" no nosso mapa (deck.gl sobre o Google Maps) —
// buraco, obra, acidente, polícia, via/faixa interditada, perigos. Ligada
// pelo menu do olho (PWA/app) ou de camadas (desktop). Ver
// lib/waze-alerts.ts (ícones/textos) e o backend src/waze/ (proxy+cache).
//
// Overlay próprio (mesmo padrão de MobileUserLocationLayer), então não
// mexe na camada de veículos. Busca só a área visível; mapa muito afastado
// (> ~90 km) não busca nada (o backend responde `tooWide`).
import { useEffect, useMemo, useRef } from "react";
import { DECK_ON_TOP } from "@/lib/google-maps-style";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { gpsDriveGet } from "@/lib/api/gpsdrive-client";
import type { MapBoundsBox } from "@/hooks/use-vehicle-clusters";
import {
  wazeAlertIconUrl,
  wazeAlertPopupHtml,
  type WazeAlert,
  type WazeAlertsResponse,
} from "@/lib/waze-alerts";

// Ícone simplificado no estilo do Waze (balão sorridente) — só um
// sinalizador visual pro item do menu.
export function WazeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        d="M12 3.2c-4.9 0-8.6 3.4-8.6 7.9 0 1.7.5 3 1.4 4-.2 1-.9 1.8-1.9 2.2 1.4.9 3.4.9 4.8.2 1.2.5 2.6.8 4.3.8 4.9 0 8.6-3.4 8.6-7.6S16.9 3.2 12 3.2Z"
        fill="#33ccff"
        stroke="#0b1f33"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="9.4" cy="9.6" r="1" fill="#0b1f33" />
      <circle cx="14.6" cy="9.6" r="1" fill="#0b1f33" />
      <path
        d="M8.8 12.4c.8 1.3 1.9 1.9 3.2 1.9s2.4-.6 3.2-1.9"
        fill="none"
        stroke="#0b1f33"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="7.6" cy="19.4" r="1.6" fill="#0b1f33" />
      <circle cx="15.8" cy="19.4" r="1.6" fill="#0b1f33" />
    </svg>
  );
}

// Arredonda pra fora numa grade de 0,05° (mesma do cache do backend) — um
// arrasto pequeno do mapa reaproveita a mesma query em vez de buscar de novo.
const GRID = 0.05;
function snapBounds(b: MapBoundsBox) {
  return {
    north: Math.ceil(b.north / GRID) * GRID,
    south: Math.floor(b.south / GRID) * GRID,
    east: Math.ceil(b.east / GRID) * GRID,
    west: Math.floor(b.west / GRID) * GRID,
  };
}

export function useWazeAlerts(bounds: MapBoundsBox | null, enabled: boolean) {
  const snapped = bounds ? snapBounds(bounds) : null;
  return useQuery({
    queryKey: [
      "waze-alerts",
      snapped?.north.toFixed(2),
      snapped?.south.toFixed(2),
      snapped?.east.toFixed(2),
      snapped?.west.toFixed(2),
    ],
    queryFn: () =>
      gpsDriveGet<WazeAlertsResponse>(
        `/api/v1/waze/alerts?north=${snapped!.north.toFixed(2)}&south=${snapped!.south.toFixed(2)}&east=${snapped!.east.toFixed(2)}&west=${snapped!.west.toFixed(2)}`,
      ),
    enabled: enabled && !!snapped,
    staleTime: 60_000,
    refetchInterval: enabled ? 90_000 : false,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function WazeAlertsLayer({
  Mod,
  bounds,
  visible,
  interleaved = true,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Mod: any;
  bounds: MapBoundsBox | null;
  visible: boolean;
  interleaved?: boolean;
}) {
  const map = Mod.useMap();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overlayRef = useRef<any>(null);
  const infoRef = useRef<google.maps.InfoWindow | null>(null);
  const { data } = useWazeAlerts(bounds, visible);
  const alerts = useMemo(() => (visible ? (data?.alerts ?? []) : []), [visible, data]);

  useEffect(() => {
    if (!map) return;
    // Mapa vetorial (interleaved): ícones sempre por cima, sem disputar
    // profundidade com ruas/nomes — sem isso os nomes "piscavam" (print
    // 2026-10-21 com o Map ID próprio). Ver DECK_ON_TOP.
    const overlay = new Mod.GoogleMapsOverlay({ interleaved, parameters: DECK_ON_TOP });
    overlay.setMap(map);
    overlayRef.current = overlay;
    return () => {
      overlay.finalize?.();
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, Mod, interleaved]);

  useEffect(() => {
    if (!visible) infoRef.current?.close();
  }, [visible]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.setProps({
      layers: [
        new Mod.IconLayer({
          id: "waze-alerts",
          data: alerts,
          pickable: true,
          sizeUnits: "pixels",
          getPosition: (a: WazeAlert) => [a.lng, a.lat],
          getSize: 36,
          getIcon: (a: WazeAlert) => ({
            url: wazeAlertIconUrl(a),
            id: wazeAlertIconUrl(a),
            width: 72,
            height: 80,
            anchorY: 74,
          }),
          onClick: (info: { object?: WazeAlert }) => {
            const a = info.object;
            if (!a || !map || typeof google === "undefined") return;
            if (!infoRef.current) infoRef.current = new google.maps.InfoWindow();
            infoRef.current.setContent(wazeAlertPopupHtml(a));
            infoRef.current.setPosition({ lat: a.lat, lng: a.lng });
            infoRef.current.setOptions({ pixelOffset: new google.maps.Size(0, -34) });
            infoRef.current.open(map);
          },
        }),
      ],
    });
  }, [alerts, Mod, map, interleaved]);

  useEffect(() => () => infoRef.current?.close(), []);

  return null;
}
